/**
 * @file BlendBookModal.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-17
 * @modified 2026-08-27
 * @description Edit the raw material book — prices or nutrient values — with the
 *              update history beside it.
 *
 *              One component serves both because they are the same job on
 *              different columns: a grid of every material against a fixed set of
 *              columns, one effective date for the lot, save the diff, and a list
 *              of past batches you can revert. Two near-identical files would
 *              drift; the `kind` prop is the only real difference.
 *
 *              Editing lives here rather than in the Materials tab because these
 *              are managed figures, not a scratchpad. The tab used one date
 *              control as BOTH the read as-of and the write effective date, so
 *              changing it silently threw away unsaved edits. Here the two are
 *              separate: the tab reads, this writes.
 *
 *              Nothing is written per keystroke. Save posts only cells that
 *              actually moved, the server stamps them with one batch_id and
 *              writes a single audit entry, and the whole batch can be reverted
 *              in one action — deleting the rows restores the previous ones,
 *              because a dated series resolves to the newest row on or before the
 *              date asked for.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import DateInput from './DateInput';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import { formatDate } from '../utils/dateFormatters';
import './BlendCalculator.css';

const today = () => new Date().toISOString().slice(0, 10);

/** Blank, '-' and '*' all mean "not quoted", which is NULL and never 0. */
function parseCell(raw) {
  const t = String(raw ?? '').trim();
  if (t === '' || t === '-' || t === '*') return null;
  const n = Number(t.replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : undefined;   // undefined = unparseable, reject
}

/** NUMERIC(12,4) arrives as "231.0000"; show it as people wrote it. */
function trim(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value);
}

const ROLE_LABEL = { spot: 'SPOT', current: 'CURRENT', upcoming: 'UPCOMING' };

function BookBody({
  kind, columns, materials, before, edits, setEdits,
  effectiveDate, setEffectiveDate, search, setSearch,
  batches, onRevert, error, saving, dirty
}) {
  useSlideOutButton([dirty, saving]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return materials;
    return materials.filter((m) =>
      `${m.name} ${m.key} ${m.supplier_code || ''}`.toLowerCase().includes(t));
  }, [materials, search]);

  const header = (c) => (kind === 'costs'
    ? (ROLE_LABEL[c.role] || c.label)
    : c.label);

  return (
    <div className="blend-book-body">
      {error && (
        <div className="blend-warning inline">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}

      <div className="blend-book-controls">
        <div className="filter-group">
          <label htmlFor="bb-date">Effective from</label>
          <DateInput id="bb-date" value={effectiveDate}
                     onChange={(e) => setEffectiveDate(e.target.value)} />
          <span className="blend-field-hint">
            The date these figures apply from. Earlier dates keep their own values.
          </span>
        </div>
        <div className="filter-group">
          <label htmlFor="bb-search">Search</label>
          <input id="bb-search" className="blend-cell-input blend-book-search"
                 type="text" value={search} placeholder="Material or code…"
                 onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="blend-book-split">
        <div className="table-container blend-book-table-wrap">
          <table className="data-table blend-book-table">
            <thead>
              <tr>
                <th>Material</th>
                {columns.map((c) => (
                  <th key={c.key} className="num">
                    {header(c)}
                    {kind === 'costs' && c.period_name && (
                      <span className="blend-col-period">{c.period_name}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id}>
                  <td>
                    <span className="blend-material-name">{m.name}</span>
                  </td>
                  {columns.map((c) => {
                    const cell = `${m.id}:${c.key}`;
                    const prior = before.get(cell);
                    const edited = cell in edits;
                    const shown = edited ? edits[cell] : trim(prior);
                    // A cell nobody has quoted reads as such rather than as an
                    // empty box that looks like it's waiting for a number.
                    const notQuoted = !edited && prior === null;
                    return (
                      <td key={c.key} className="num">
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`blend-cell-input${edited ? ' edited' : ''}`}
                          value={shown}
                          placeholder={notQuoted ? 'not quoted' : ''}
                          onChange={(e) => setEdits((prev) => ({ ...prev, [cell]: e.target.value }))}
                          aria-label={`${header(c)} for ${m.name}`}
                        />
                        {edited && prior !== undefined && (
                          <span className="blend-book-was">was {prior === null ? '—' : trim(prior)}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={columns.length + 1} className="muted">
                  No materials match that search.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <aside className="blend-book-history">
          <h4 className="blend-panel-title">Update history</h4>
          {!batches.length && (
            <p className="blend-book-empty">
              No updates recorded yet. Saving here creates one.
            </p>
          )}
          {batches.map((b) => (
            <div key={b.batch_id} className="blend-book-batch">
              <div>
                <strong>{formatDate(b.effective_date)}</strong>
                <span className="blend-book-batch-meta">
                  {b.kind} · {b.row_count} row{b.row_count === 1 ? '' : 's'}
                  {b.created_at ? ` · saved ${formatDate(b.created_at)}` : ''}
                </span>
              </div>
              <button className="btn-icon" title="Revert this update"
                      onClick={() => onRevert(b)}>
                <span className="material-symbols-rounded">undo</span>
              </button>
            </div>
          ))}
          <p className="blend-book-note">
            Reverting removes that update's rows. Whatever applied before it
            takes effect again.
          </p>
        </aside>
      </div>
    </div>
  );
}

export default function BlendBookModal({ kind, book, onClose, onSaved }) {
  const isCosts = kind === 'costs';
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState({});
  const [batches, setBatches] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(null);

  // Memoised, not derived inline: a fresh `[]` each render would invalidate the
  // `before` map and the save callback on every keystroke.
  const columns = useMemo(
    () => (isCosts ? (book?.columns || []) : (book?.nutrient_components || [])),
    [book, isCosts]);
  const materials = useMemo(() => book?.materials || [], [book]);

  /**
   * The value each cell resolves to today, keyed "materialId:columnKey".
   *
   * `undefined` (absent from the map) and `null` mean different things: absent is
   * "never recorded", null is "recorded as not quoted". The distinction drives
   * both the placeholder and the "was" note, so it must survive.
   */
  const before = useMemo(() => {
    const map = new Map();
    if (isCosts) {
      const byId = new Map((book?.columns || []).map((c) => [c.id, c.key]));
      for (const row of book?.costs || []) {
        const key = byId.get(row.price_column_id);
        if (key) map.set(`${row.material_id}:${key}`, row.cost_per_tonne);
      }
    } else {
      for (const row of book?.nutrients || []) {
        for (const c of columns) {
          const v = row.values?.[c.key];
          if (v !== undefined) map.set(`${row.material_id}:${c.key}`, v);
        }
      }
    }
    return map;
  }, [book, columns, isCosts]);

  const loadBatches = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/blends/book/batches?limit=25`,
        { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setBatches((data.batches || []).filter((b) => b.kind === kind));
    } catch { /* history is a convenience; its absence must not block editing */ }
  }, [kind]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  /** Only cells that actually moved. Re-typing the same number is not a change. */
  const changed = useMemo(() => {
    const out = [];
    for (const [cell, raw] of Object.entries(edits)) {
      const parsed = parseCell(raw);
      if (parsed === undefined) continue;                 // unparseable, skip
      const prior = before.has(cell) ? before.get(cell) : undefined;
      const priorNum = prior === null || prior === undefined ? null : Number(prior);
      if (priorNum === parsed) continue;
      const [materialId, columnKey] = cell.split(':');
      out.push({ materialId: Number(materialId), columnKey, value: parsed });
    }
    return out;
  }, [edits, before]);

  const bad = useMemo(
    () => Object.entries(edits).filter(([, raw]) => parseCell(raw) === undefined).length,
    [edits]);

  const save = useCallback(async () => {
    if (bad) { setError(`${bad} cell(s) aren't a number. Fix or clear them first.`); return; }
    if (!changed.length) { setError('Nothing has changed.'); return; }

    setSaving(true);
    setError('');
    try {
      let body;
      if (isCosts) {
        const byKey = new Map(columns.map((c) => [c.key, c.id]));
        body = {
          effective_date: effectiveDate,
          source: 'manual',
          rows: changed.map((c) => ({
            material_id: c.materialId,
            price_column_id: byKey.get(c.columnKey),
            cost_per_tonne: c.value
          }))
        };
      } else {
        // Nutrients are stored as one JSONB row per material, so the changes are
        // grouped by material and merged over whatever that material already
        // resolves to — otherwise saving one nutrient would blank the others.
        const grouped = new Map();
        for (const c of changed) {
          if (!grouped.has(c.materialId)) {
            const existing = {};
            for (const col of columns) {
              const v = before.get(`${c.materialId}:${col.key}`);
              if (v !== undefined && v !== null) existing[col.key] = Number(v);
            }
            grouped.set(c.materialId, existing);
          }
          const values = grouped.get(c.materialId);
          if (c.value === null) delete values[c.columnKey];
          else values[c.columnKey] = c.value;
        }
        body = {
          effective_date: effectiveDate,
          source: 'manual',
          rows: [...grouped].map(([material_id, values]) => ({ material_id, values }))
        };
      }

      const res = await fetch(
        `${API_URL}/api/blends/book/${isCosts ? 'costs' : 'nutrients'}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body)
        });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');

      setEdits({});
      await loadBatches();
      onSaved(
        `Saved ${changed.length} change${changed.length === 1 ? '' : 's'} `
        + `effective ${formatDate(effectiveDate)}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [bad, changed, columns, before, effectiveDate, isCosts, loadBatches, onSaved]);

  const revert = useCallback(async (batch) => {
    setConfirmRevert(null);
    try {
      const res = await fetch(`${API_URL}/api/blends/book/batches/${batch.batch_id}`,
        { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Revert failed');
      await loadBatches();
      onSaved(`Reverted the update effective ${formatDate(batch.effective_date)}.`);
    } catch (e) {
      setError(e.message);
    }
  }, [loadBatches, onSaved]);

  const dirty = changed.length;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isCosts ? 'Raw material prices' : 'Raw material analysis'}
      size="xl"
      className="blend-book"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={() => setEdits({})}
                    disabled={!Object.keys(edits).length || saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">undo</span></div>
              <span className="btn-label">Discard</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={save} disabled={!dirty || saving}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>
                  {saving ? 'progress_activity' : 'save'}
                </span>
              </div>
              <span className="btn-label">
                {saving ? 'Saving…' : dirty ? `Save ${dirty}` : 'Save'}
              </span>
            </button>
          </div>
        </div>
      }
    >
      <BookBody
        kind={kind}
        columns={columns}
        materials={materials}
        before={before}
        edits={edits}
        setEdits={setEdits}
        effectiveDate={effectiveDate}
        setEffectiveDate={setEffectiveDate}
        search={search}
        setSearch={setSearch}
        batches={batches}
        onRevert={setConfirmRevert}
        error={error}
        saving={saving}
        dirty={dirty}
      />

      {confirmRevert && (
        <ConfirmDialog
          isOpen
          title="Revert this update?"
          message={
            `The ${confirmRevert.row_count} row(s) saved effective `
            + `${formatDate(confirmRevert.effective_date)} will be removed, and whatever `
            + 'applied before takes effect again. This cannot be undone.'
          }
          confirmText="Revert"
          onConfirm={() => revert(confirmRevert)}
          onCancel={() => setConfirmRevert(null)}
        />
      )}
    </Modal>
  );
}
